import Vue from 'vue';
import ConfirmModal from '@/components/Common/ConfirmModal.vue';
import MessageModal from '@/components/Common/MessageModal.vue';
import Dialog from '@components/Common/Dialog';
import ErrorDialogContent from '@components/Common/ErrorDialogContent';

import {
    first,
    pluck,
    filter,
    map,
    distinctUntilChanged,
    withLatestFrom,
} from 'rxjs/operators';

import {
    result as utilResult,
    setLocalStorage,
    getLang,
    formatNumber,
    formatCurrency,
    isValueExist,
    getAdrielJwt,
} from '@util';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { i18n } from '../main';
import { fromEvent, merge } from 'rxjs';

import { ACCOUNT_TYPES, IFRAME_EVENT_MESSAGE } from '@consts';

import memoize from 'lodash/memoize';
import getSymbolFromCurrency from 'currency-symbol-map';
import memoizee from 'memoizee';

import { NOTI_TYPES } from '@/store/dashboardEdit';
import '@/components/Common/UiTooltip';

import {
    DashboardQueryClient,
    // DashboardClient,
} from 'adriel_backandfrontcommon';

import { loadLanguageAsync } from '../i18n';

import DashboardService from '@/services/DashboardService';
import { AuthService } from '@/services';
import { loadProductFruitsChatBot } from '@/utils/dashboard';
import { BreakPoint, pickBreakpoint } from '@/utils/layout';

const WATCH_DEFAULT = R.mergeRight(Object.freeze({ immediate: true }));

const REACT_DASHBOARD_PAGES = new Set([
    'ReactDashboard',
    'ReactDashboardSharedView',
]);

const CUSTOM_FIELD_ACCOUNT_IDS = [990961, 15167, 755681, 801, 1344441, 1724741];

export default {
    computed: {
        ...mapState('user', [
            'subscription',
            'accountFetched$',
            'dashboardClient',
            'dashboardClientCreated$',
        ]),
        ...mapGetters('user', {
            globalUser: 'user',
            userLang: 'userLang',
            cAccountId: 'accountId',
            globalAccount: 'account',
            planType: 'planType',
            isNicePg: 'isNicePg',
            subscriptionPlan: 'subscriptionPlan',
            subscriptionStatusType: 'subscriptionStatusType',
            isQa: 'isQa',
        }),
        ...mapGetters('view', ['windowDimension']),
        ...mapGetters('newDashboard', ['getCellType']),
        language: R.compose(getLang, R.prop('globalUser')),
        currency: R.pathOr('USD', ['globalAccount', 'currency']),
        isDemo() {
            return this.globalUser && this.globalUser.type === 'DEMO';
        },
        maybeKorean() {
            const country = R.compose(
                R.equals('KR'),
                R.toUpper,
                R.pathOr('US', ['globalUser', 'country'])
            )(this);

            const lang = R.compose(
                R.equals('KO'),
                R.toUpper,
                R.defaultTo(''),
                R.prop('userLang')
            )(this);

            return country || lang;
        },
        isMobileSize() {
            return this.windowDimension.width < pickBreakpoint(BreakPoint.md);
        },
        currencyFormatter() {
            const { globalUser: user, globalAccount: account } = this;

            return !user && !account ? R.identity : formatCurrency;
        },
        isPlanT() {
            return this.isEligibleUser(ACCOUNT_TYPES.PLAN_T);
        },
        isPlanA() {
            return this.isEligibleUser(ACCOUNT_TYPES.PLAN_A);
        },
        isReactDashboard() {
            // const { name = '' } = this.$route;
            // return REACT_DASHBOARD_PAGES.has(name);
            const isVueStandAlone =
                process.env.VUE_APP_IS_VUE_STANDALONE === 'true';

            return !isVueStandAlone;
        },
        isWhitelabelUrl() {
            const adrielDomainRegex = /(.*)(adriel\.com)$/;
            const hostname = window.location.hostname;

            return (
                hostname !== 'localhost' && !adrielDomainRegex.test(hostname)
            );
        },
        isCustomFieldUser() {
            return CUSTOM_FIELD_ACCOUNT_IDS.includes(Number(this.cAccountId));
        },
    },
    methods: {
        ...mapMutations('view', [
            'setDemoAccountModalActive',
            'setToast',
            'setIsSubscriptionModalActive',
            'addNotiMsg',
        ]),
        ...mapMutations('user', ['setDashboardClient']),
        ...mapActions('iframe', ['postIframeMessage']),
        showDemoAccountModal() {
            this.setDemoAccountModalActive(true);
        },
        confirmBefore({
            text,
            confirmText,
            cancelText,
            confirmCb = R.identity,
            cancelCb = R.identity,
            className,
            children,
        }) {
            let instance;

            const confirmModalClass = Vue.extend(ConfirmModal);
            const destroyInstance = () => {
                instance.$destroy(true);
                this.$el.removeChild(instance.$el);
            };
            const _confirmCb = () => {
                confirmCb();
                destroyInstance();
            };

            const _cancelCb = () => {
                cancelCb();
                destroyInstance();
            };
            instance = new confirmModalClass({
                propsData: {
                    active: true,
                    text,
                    confirmText,
                    cancelText,
                    className,
                    confirmCb: _confirmCb,
                    cancelCb: _cancelCb,
                    children,
                },
                i18n,
            });

            instance.$mount();
            this.$el.appendChild(instance.$el);
        },
        messageBefore(propsData) {
            const modal = Vue.extend(MessageModal);
            const instance = new modal({
                propsData: R.merge({ active: true }, propsData),
                i18n,
            });
            const destroyInstance = () => {
                instance.$destroy(true);
                instance.$el.remove();
            };
            instance.$on('btnClicked', () => {
                destroyInstance();
                utilResult('cb', propsData);
            });
            instance.$on('close', destroyInstance);

            instance.$mount();
            this.$el.appendChild(instance.$el);
            return instance;
        },
        toast: (() => {
            let instance;

            fromEvent(document.body, 'click')
                .pipe(
                    pluck('target'),
                    filter((target) => {
                        const elem = R.prop('$el', instance || {});

                        do {
                            if (elem === target) {
                                return true;
                            }
                            target = target.parentNode;
                        } while (target && elem);

                        return false;
                    })
                )
                .subscribe(() => utilResult('$el.remove', instance));

            return function (
                message = 'message',
                position = 'is-top',
                type = 'adriel-warning',
                duration = 2000
            ) {
                utilResult('$el.remove', instance);

                instance = this.$toast.open({
                    duration,
                    message,
                    position,
                    type: 'adriel-toast ' + type,
                    queue: false,
                });
            };
        })(),
        redesignedToast({ message, time, isError = false, style, position }) {
            this.setToast({
                message,
                time,
                isError,
                style,
                position,
            });
        },
        globalToast: (() => {
            let instance;

            fromEvent(document.body, 'click')
                .pipe(
                    pluck('target'),
                    filter((target) => {
                        const elem = R.prop('$el', instance || {});

                        do {
                            if (elem === target) {
                                return true;
                            }
                            target = target.parentNode;
                        } while (target && elem);

                        return false;
                    })
                )
                .subscribe(() => utilResult('$el.remove', instance));

            return function (
                message = 'message',
                position = 'is-top',
                type = 'adriel-warning',
                duration = 2000
            ) {
                utilResult('$el.remove', instance);

                instance = this.$toast.open({
                    duration,
                    message,
                    position,
                    type: 'global-toast ' + type,
                    queue: false,
                });
            };
        })(),
        openHelp() {
            window.open('/#/helpCampaign', '_blank');
        },
        blurElement() {
            //blur if there is active element
            utilResult('activeElement.blur', document);
        },
        getBeforeDestroy$() {
            return this.$eventToObservable('hook:beforeDestroy').pipe(first());
        },
        setLang(lang, name) {
            const checkedLang = lang || getLang();
            if (name) {
                const currentQuery = this.$route.query;
                this.$router.replace({
                    name,
                    query: R.assoc('lang', checkedLang, currentQuery),
                });
            }

            setLocalStorage('lang', checkedLang);

            loadLanguageAsync(checkedLang);
        },
        isLangType(type) {
            return this.userLang === type;
        },
        pushToRoute(path) {
            path && this.$router.push(path);
        },
        toFixed(value, length = 2) {
            return Number(value).toFixed(length);
        },
        formatCurrency(nb, options = {}) {
            return this.currencyFormatter(nb, {
                currency: this.currency,
                ...options,
            });
        },
        getCurrencySymbol(receivedCurrency) {
            const currency = receivedCurrency || this.currency;
            switch (currency) {
                case 'USD':
                    return 'US$';
                default:
                    return getSymbolFromCurrency(currency) || '$';
            }
        },
        formatNumber: formatNumber,
        queryStringThenRemove(queryName) {
            const query = R.path(['$route', 'query'], this);
            this.$router.replace({ query: R.omit([queryName], query) });
            return R.prop(queryName, query);
        },
        inputEmitter(v) {
            return this.$emit('input', v);
        },
        changeAccount(accountId, canStay = true) {
            const router = this.$route;
            if (accountId != this.cAccountId) {
                let query = R.assoc('accountId', accountId, router.query);
                query = R.dissoc('userAccountId', query);

                if (this.$route.name === 'Dashboard') {
                    // add if wanna keep query
                    query = R.pick(['accountId'], query);

                    const { href } = this.$router.resolve({
                        path: '/dashboard',
                        query,
                    });

                    window.open(href, '_blank');
                } else {
                    const { href } = this.$router.resolve({
                        path: canStay ? router.path : '/myCampaigns',
                        query,
                    });

                    window.open(href, '_blank');
                }
            }
        },
        tooltipForDesktop(content) {
            const obj = {
                content,
            };

            if (this.windowDimension.width < 768) {
                return R.assoc('trigger', 'manual', obj);
            }

            return obj;
        },
        dialog({
            confirmText,
            cancelText,
            confirmType,
            cancelType,
            confirmCb = R.identity,
            cancelCb = R.identity,
            child,
            hasConfirmButton,
            hasCancelButton,
            text,
            title,
            hideButtons,
            buttonDisabled,
            paddingLess,
            slots = {},
            backdrop,
            keyboard,
            scrollOnBody,
            hasCloseButton = false,
            hasContentsMaxWidth = true,
            autoSize = false,
            newDesign = false,
            customCss,
        } = {}) {
            let instance;
            const body$ = window.document.body;

            const dialog = Vue.extend(Dialog);
            const destroyInstance = () => {
                instance.$destroy(true);
                try {
                    body$.removeChild(instance.$el);
                } catch (e) {
                    console.log(e);
                }
            };
            const _confirmCb = () => {
                confirmCb();
                destroyInstance();
            };

            const _cancelCb = () => {
                cancelCb();
                destroyInstance();
            };

            instance = new dialog({
                propsData: {
                    active: true,
                    child,
                    confirmText,
                    cancelText,
                    confirmCb: _confirmCb,
                    cancelCb: _cancelCb,
                    confirmType,
                    cancelType,
                    hasConfirmButton,
                    hasCancelButton,
                    text,
                    title,
                    hideButtons,
                    buttonDisabled,
                    paddingLess,
                    backdrop,
                    keyboard,
                    scrollOnBody,
                    hasCloseButton,
                    hasContentsMaxWidth,
                    autoSize,
                    newDesign,
                    customCss,
                },
                i18n,
            });

            instance.$slots = slots;

            instance.$on('update:active', (value) => {
                !value && _cancelCb();
            });

            instance.$mount();
            body$.appendChild(instance.$el);

            return {
                instance,
                destroy: destroyInstance,
            };
        },
        srcWithAccount(src) {
            if (!src) return;
            return `${src}?accountId=${this.cAccountId}`;
        },
        showErrorDialog({
            mainText = '',
            errorText = '',
            onConfirm = R.identity,
        }) {
            mainText =
                mainText || this.$t('NEW_DASHBOARD.publishing_error_general');

            this.dialog({
                confirmText: this.$t('COMMON.go_back'),
                confirmType: 'secondary',
                hasCancelButton: false,
                child: (
                    <ErrorDialogContent
                        mainText={mainText}
                        errorText={errorText}
                    />
                ),
                confirmCb: onConfirm,
            });
        },
        clipHtml(value) {
            const classList = document.querySelector('html').classList;
            value
                ? classList.add('is-clipped')
                : classList.remove('is-clipped');
        },
        generateWatchNew$(key, option = {}) {
            option = WATCH_DEFAULT(option);
            return this.$watchAsObservable(key, option).pipe(pluck('newValue'));
        },
        translateOr(key, value = '') {
            return this.$te(key) ? this.$t(key) || value : value;
        },
        mTranslateOr: memoize(function (key, value = '') {
            return this.translateOr(key, value);
        }),
        openInNewTab(path) {
            const routeData = this.$router.resolve(path);
            window.open(routeData.href, '_blank');
        },
        wrapTooltip({
            visible,
            content,
            placement = 'top',
            title,
            className = '',
            overlayClassName = '',
            delay = 0,
            getPopupContainer = R.always(document.body),
            innerClassName = '',
            trigger = 'hover',
            hasLink = false,
            key,
            autoAdjustOverflow = false,
            align,
        } = {}) {
            className = `adriel-tooltip-slot-content ${className}`;

            if (visible) {
                return (
                    <a-tooltip
                        placement={placement}
                        mouseLeaveDelay={hasLink ? 0.1 : delay}
                        overlayClassName={overlayClassName}
                        getPopupContainer={getPopupContainer}
                        class={innerClassName}
                        trigger={trigger}
                        key={key}
                        autoAdjustOverflow={autoAdjustOverflow}
                        align={align}
                    >
                        <div class={className} slot="title">
                            {hasLink ? (
                                <div domPropsInnerHTML={title}></div>
                            ) : (
                                title
                            )}
                        </div>
                        {content}
                    </a-tooltip>
                );
            } else {
                return content;
            }
        },
        wrapUiTooltip({ content, title, key, style } = {}) {
            return (
                <ui-tooltip content={title} key={key} style={style}>
                    {content}
                </ui-tooltip>
            );
        },
        isEligibleUser(eligibleType) {
            const accountType = this.planType;
            let isEligible = false;

            if (eligibleType instanceof Array) {
                for (let userType of eligibleType) {
                    if (accountType === userType) isEligible = true;
                }
            } else {
                isEligible = accountType === eligibleType;
            }

            return isEligible;
        },
        notEnoughPermissionToast({ message = '', time } = {}) {
            message = message || this.$t('COMMON.permission_not_enough');
            return this.addNotiMsg({
                type: NOTI_TYPES.FAIL,
                title: message,
            });
        },
        showAppsumoLimitReachedToast() {
            return this.globalToast(
                this.$t('PAYMENT.subscription_appsumo_limited_msg', {
                    invoice_item_uuid: R.prop(
                        'invoice_item_uuid',
                        this.subscription
                    ),
                }),
                undefined,
                undefined,
                5000
            );
        },
        renderWarningToLimiedUser() {
            this.setIsSubscriptionModalActive(true);
        },
        ifPermission({ hasPermission = R.T, fn = R.identity } = {}) {
            return hasPermission() ? fn() : this.notEnoughPermissionToast();
        },
        getAvailableFieldsV2({
            adAccounts,
            isCustomTable,
            currentBreakdowns,
            isForDatastream,
        }) {
            const { userLang: language, isQa } = this;

            const getAssetIds = R.compose(
                R.uniq,
                R.filter(R.identity),
                R.pluck('assetId')
            );

            const assetIds = R.ifElse(
                isValueExist,
                getAssetIds,
                R.identity
            )(adAccounts);

            return DashboardService.getAvailableFieldsV2({
                assetIds,
                language,
                widgetType: isCustomTable ? 'normal' : 'marketing',
                breakdowns: currentBreakdowns,
                include: isQa ? ['locale', 'assetId'] : undefined,
                isForDatastream,
            });
        },
        getAvailableFields: memoizee(
            function ({
                adAccounts,
                isCustomTable = false,
                currentBreakdowns,
                groupByDataSource = false,
                useObjectFormat = false,
            }) {
                if (!isValueExist(adAccounts)) return Promise.resolve([]);

                return this.dashboardClient
                    .getAvailableFields({
                        adAccounts,
                        isCustomTable,
                        currentBreakdowns,
                        groupByDataSource,
                        useObjectFormat,
                    })
                    .then(R.identity);
            },
            {
                normalizer: R.compose(
                    JSON.stringify,
                    R.evolve({
                        adAccounts: R.compose(
                            R.sort(R.comparator(R.gt)),
                            R.pluck('channelId')
                        ),
                    }),
                    R.head
                ),
                promise: true,
            }
        ),
        createClient$() {
            const { accountFetched$ } = this;

            const accountId$ = accountFetched$.pipe(
                map(Number),
                filter(R.identity),
                distinctUntilChanged()
            );

            const token$ = AuthService.setAccessToken$.pipe(
                distinctUntilChanged()
            );

            this.$subscribeTo(
                merge(
                    accountId$,
                    token$.pipe(withLatestFrom(accountId$), map(R.last))
                ),
                (accountId) => {
                    let token = getAdrielJwt() || undefined;

                    if (token) {
                        token = 'Bearer ' + token;
                    }

                    try {
                        const dashboardClient = new DashboardQueryClient({
                            apiUrl: process.env.VUE_APP_API_URL,
                            accountId,
                            useWorker: navigator?.hardwareConcurrency,
                            token,
                        });

                        this.setDashboardClient(dashboardClient);
                        this.dashboardClientCreated$.next(true);
                    } catch (e) {
                        console.error('dashboardClientError', e);
                    }
                }
            );
        },
        loadChatbot(isHelpCenterOpen) {
            window.productFruits?.api?.inAppCenter?.show();

            // Remove hide styles added by hideChatbot()
            document
                .querySelectorAll('[id="productfruits-announcement-hide"]')
                .forEach((el) => el.remove());

            const existingStyleForShow = document.getElementById(
                'productfruits-announcement-show'
            );
            if (!existingStyleForShow) {
                const style = document.createElement('style');
                style.id = 'productfruits-announcement-show';
                style.textContent = `
                .productfruits--feedback-popup {
                    bottom: 74px !important;
                    }
                `;
                document.head.appendChild(style);
            }

            // Product Fruits announcements CSS 복원
            const existingStyleForHide = document.getElementById(
                'productfruits-announcement-hide'
            );
            if (existingStyleForHide) {
                existingStyleForHide.remove();
            }

            const chatConatiner = document.getElementById(
                'hubspot-messages-iframe-container'
            );

            if (chatConatiner) {
                chatConatiner.style.visibility = 'visible';
            }

            this.postIframeMessage({
                type: IFRAME_EVENT_MESSAGE.SYNC_CHATBOT_VISIBILITY,
                origin: `VUE`,
                data: {
                    value: true,
                },
            });

            if (isHelpCenterOpen) {
                window.HubSpotConversations?.widget?.open();
            }

            loadProductFruitsChatBot(isHelpCenterOpen);
        },
        hideChatbot() {
            window.productFruits?.api?.inAppCenter?.hide();

            const style = document.createElement('style');
            style.id = 'productfruits-announcement-hide';
            style.textContent = `
                .productfruits--container,
                .pfruits-toasts-holder {
                    display: none !important;
                }

                
                .productfruits--feedback-popup {
                    bottom: 74px !important;
                }
            `;
            document.head.appendChild(style);

            const chatConatiner = document.getElementById(
                'hubspot-messages-iframe-container'
            );

            if (chatConatiner) {
                chatConatiner.style.visibility = 'hidden';
            }

            this.postIframeMessage({
                type: IFRAME_EVENT_MESSAGE.SYNC_CHATBOT_VISIBILITY,
                origin: `VUE`,
                data: {
                    value: false,
                },
            });
        },
    },
};
